import { FormLayout, TextInput } from '@kcftech/design-system';
import { UploadFileInfo } from '@progress/kendo-react-upload';
import { ValidatableLabel } from 'components/forms/ValidatableLabel';
import { WizardContent } from 'components/wizard/WizardContent';
import { SourceCodeInput } from 'pages/Models/AnalyticsModelTab/SourceCodeInput';
import {
    getSourceCodeRules,
    timeIntervalRules,
} from 'pages/Models/AnalyticsModelTab/shared';
import { useMemo } from 'react';
import { Controller, RegisterOptions, useForm } from 'react-hook-form';
import { isModelUnique } from 'utils/analytics_model';

export type CreateModelStep1FormData = Readonly<{
    name: string;
    version: string;
    sourceCode: UploadFileInfo | undefined;
    timeIntervalMinutes: string | undefined;
}>;

const nameRules: RegisterOptions = {
    required: {
        value: true,
        message: 'Model name is required.',
    },
    maxLength: {
        value: 50,
        message: 'Model name too long.',
    },
};

const versionRules: RegisterOptions = {
    required: {
        value: true,
        message: 'Model version is required.',
    },
    maxLength: {
        value: 50,
        message: 'Model version too long.',
    },
};

const sourceCodeRules = getSourceCodeRules(true);

const validateModelUnique = async (
    name: string | undefined,
    version: string | undefined,
    corporationId?: string,
) =>
    !name || !version || (await isModelUnique({ name, version, corporationId }))
        ? undefined
        : 'Model with version already exists.';

export type CreateModelStep1Props = Readonly<{
    corporationId?: string;
    data: CreateModelStep1FormData;
    onCancel: () => void;
    onNext: (data: CreateModelStep1FormData) => void;
}>;

export function CreateModelStep1({
    corporationId,
    data,
    onCancel,
    onNext,
}: CreateModelStep1Props) {
    const {
        control,
        formState: { errors, isValidating, isSubmitting, isSubmitted, isValid },
        getValues,
        handleSubmit,
        register,
    } = useForm<CreateModelStep1FormData>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: data,
        shouldUnregister: true,
    });

    const allNameRules = useMemo<RegisterOptions>(
        () => ({
            ...nameRules,
            validate: (name: string) =>
                validateModelUnique(name, getValues('version'), corporationId),
        }),
        [corporationId, getValues],
    );

    const allVersionRules = useMemo<RegisterOptions>(
        () => ({
            ...versionRules,
            validate: (version: string) =>
                validateModelUnique(getValues('name'), version, corporationId),
        }),
        [corporationId, getValues],
    );

    return (
        <WizardContent
            canCancel={!isSubmitting}
            canProceed={!isValidating && !isSubmitting && !(isSubmitted && !isValid)}
            onCancel={onCancel}
            onNext={handleSubmit(onNext)}
        >
            <FormLayout>
                <TextInput
                    label="Name"
                    required
                    error={errors.name?.message}
                    placeholder="Enter a model name"
                    {...register('name', allNameRules)}
                />
                <TextInput
                    label="Model Version"
                    required
                    placeholder="Enter a version number"
                    error={errors.version?.message}
                    {...register('version', allVersionRules)}
                />
                <ValidatableLabel
                    label="Model Source Code"
                    className="flex-row"
                    required
                    error={errors.sourceCode?.message}
                >
                    <Controller
                        name="sourceCode"
                        control={control}
                        render={({ field }) => (
                            <SourceCodeInput
                                value={field.value}
                                onChange={field.onChange}
                            />
                        )}
                        rules={sourceCodeRules}
                    />
                </ValidatableLabel>

                <TextInput
                    required
                    label="Time Interval (minutes)"
                    placeholder="Enter time interval"
                    error={errors.timeIntervalMinutes?.message}
                    {...register('timeIntervalMinutes', timeIntervalRules)}
                />
            </FormLayout>
        </WizardContent>
    );
}
